/**
 * Stealth Profile Panel - Ship Signature Capabilities
 * 
 * Displays qualitative stealth information based on ship's armor signature multipliers.
 * This replaces the calculated signature display since we don't have accurate
 * power-to-signature formulas.
 * 
 * Shows:
 * - Stealth tier rating based on base signature multipliers
 * - Relative stealth capabilities (what the ship is good/bad at)
 * - Component analysis (coolers, power plants, etc.)
 * - Comparative context (similar ships)
 */

import React, { useMemo } from 'react';
import type { Ship, ShipHardpoint } from '../../services/shipService';
import type { ComponentItem } from '../../services/componentService';
import { Radio, Thermometer, Target, Shield, Eye, AlertTriangle, Snowflake, Zap, Info } from 'lucide-react';
import styles from './StealthProfilePanel.module.css';

interface StealthProfilePanelProps {
  ship: Ship;
  hardpoints: ShipHardpoint[];
  hardpointOverrides?: Map<number, ComponentItem>;
  /** Show notice that component swaps don't affect values (for loadout builder) */
  showComponentSwapNotice?: boolean;
}

// Calculate overall stealth tier from signature multipliers
const calculateStealthTier = (
  em: number | null,
  ir: number | null,
  cs: number | null
): { tier: string; tierClass: string; description: string; color: string; score: number } => {
  // Default to 1.0 if null (standard signature)
  const emValue = em ?? 1.0;
  const irValue = ir ?? 1.0;
  const csValue = cs ?? 1.0;

  // Calculate composite score (lower = better stealth)
  // Weight: EM 35%, IR 35%, CS 30%
  const compositeScore = (emValue * 0.35) + (irValue * 0.35) + (csValue * 0.30);

  if (compositeScore <= 0.70) {
    return { 
      tier: 'S-Tier', 
      tierClass: 'sTier',
      description: 'Elite Stealth - Designed for covert operations. Extremely hard to detect (e.g., Sabre, Eclipse, Prowler)',
      color: '#00ff88',
      score: compositeScore
    };
  } else if (compositeScore <= 0.85) {
    return { 
      tier: 'A-Tier', 
      tierClass: 'aTier',
      description: 'Excellent Stealth - Superior signature reduction, ideal for reconnaissance and combat (e.g., Hornet Ghost, Razor EX)',
      color: '#00d4ff',
      score: compositeScore
    };
  } else if (compositeScore <= 0.95) {
    return { 
      tier: 'B-Tier', 
      tierClass: 'bTier',
      description: 'Good Stealth - Above-average signature management, balanced for combat and stealth (e.g., Vanguard Sentinel)',
      color: '#88cc00',
      score: compositeScore
    };
  } else if (compositeScore <= 1.05) {
    return { 
      tier: 'C-Tier', 
      tierClass: 'cTier',
      description: 'Standard Profile - Average detectability, no stealth advantages or penalties (e.g., Hornet, Gladius, most ships)',
      color: '#ffaa00',
      score: compositeScore
    };
  } else if (compositeScore <= 1.20) {
    return { 
      tier: 'D-Tier', 
      tierClass: 'dTier',
      description: 'High Visibility - Above average signatures, easier to detect than standard ships (e.g., Cutlass, Freelancer)',
      color: '#ff6600',
      score: compositeScore
    };
  } else {
    return { 
      tier: 'F-Tier', 
      tierClass: 'fTier',
      description: 'Very High Signatures - Large industrial/cargo ships, very easy to detect (e.g., Caterpillar, Hull Series)',
      color: '#ff3333',
      score: compositeScore
    };
  }
};

// Calculate effective cross section for stealth ranking
const calculateEffectiveCrossSection = (
  baseX: number | null,
  baseY: number | null,
  baseZ: number | null,
  multiplier: number | null
): number | null => {
  if (!baseX || !baseY || !baseZ) return null;
  
  const actualCrossSection = Math.max(baseX, baseY, baseZ);
  const csMultiplier = multiplier ?? 1.0;
  return actualCrossSection * csMultiplier;
};

// Get signature modifier description
const getSignatureDescription = (value: number | null, type: 'EM' | 'IR' | 'CS'): string => {
  if (value === null || value === 1.0) {
    return 'Baseline signature - no hull modification (same as most ships)';
  }
  
  const percentage = Math.abs(Math.round((1 - value) * 100));
  
  if (value < 0.7) {
    return `${percentage}% harder to detect (excellent stealth)`;
  } else if (value < 0.9) {
    return `${percentage}% harder to detect (good stealth)`;
  } else if (value < 1.1) {
    return `Standard baseline detection - same as most ships (1.00× = no modifier)`;
  } else if (value < 1.3) {
    return `${Math.round((value - 1) * 100)}% easier to detect (larger signature)`;
  } else {
    return `${Math.round((value - 1) * 100)}% easier to detect (very large signature)`;
  }
};

// Analyze component loadout for stealth implications
const analyzeComponents = (hardpoints: ShipHardpoint[], hardpointOverrides?: Map<number, ComponentItem>) => {
  const coolers = hardpoints.filter(hp => {
    const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
    return item?.type === 'Cooler';
  });

  const powerPlants = hardpoints.filter(hp => {
    const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
    return item?.type === 'PowerPlant';
  });

  const shields = hardpoints.filter(hp => {
    const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
    return item?.type === 'Shield';
  });

  const weapons = hardpoints.filter(hp => {
    const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
    const type = item?.type;
    return type === 'WeaponGun' || type === 'Turret' || type === 'WeaponMissile';
  });

  // Calculate total cooling capacity
  const totalCooling = coolers.reduce((sum, hp) => {
    const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
    return sum + (item?.coolerStats?.coolingRate || 0);
  }, 0);

  // Calculate total power output
  const totalPower = powerPlants.reduce((sum, hp) => {
    const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
    return sum + (item?.powerPlantStats?.powerOutput || 0);
  }, 0);

  return {
    coolers: {
      count: coolers.length,
      totalCooling,
      avgPerCooler: coolers.length > 0 ? totalCooling / coolers.length : 0
    },
    powerPlants: {
      count: powerPlants.length,
      totalPower,
      avgSize: powerPlants.length > 0 
        ? powerPlants.reduce((sum, hp) => {
            const item = hardpointOverrides?.get(hp.id) || hp.itemDefinition;
            return sum + (item?.size || 0);
          }, 0) / powerPlants.length 
        : 0
    },
    shields: shields.length,
    weapons: weapons.length
  };
};

// Get stealth strengths and weaknesses
const getStealthCharacteristics = (
  em: number | null,
  ir: number | null,
  cs: number | null
): { strengths: string[]; weaknesses: string[]; notes: string[] } => {
  const emValue = em ?? 1.0;
  const irValue = ir ?? 1.0;
  const csValue = cs ?? 1.0;

  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const notes: string[] = [];

  // EM Analysis
  if (emValue < 0.8) {
    strengths.push('Reduced electromagnetic signature (good for radar evasion)');
  } else if (emValue > 1.2) {
    weaknesses.push('High electromagnetic signature (easier to detect on radar)');
  }

  // IR Analysis
  if (irValue < 0.8) {
    strengths.push('Excellent heat masking (stealth-capable hull)');
  } else if (irValue > 1.2) {
    weaknesses.push('High thermal signature (heat management challenges)');
  }

  // CS Analysis
  if (csValue < 0.8) {
    strengths.push('Small radar cross-section (compact hull design)');
  } else if (csValue > 1.2) {
    weaknesses.push('Large radar cross-section (highly visible to radar)');
  }

  // Composite analysis
  if (emValue < 1.0 && irValue < 1.0 && csValue < 1.0) {
    notes.push('All signatures below standard - designed for stealth operations');
  }

  if (emValue < 1.0 && irValue < 1.0) {
    notes.push('Good balance of EM and IR stealth - versatile for various missions');
  }

  if (strengths.length === 0 && weaknesses.length === 0) {
    notes.push('Standard signature profile - no particular stealth advantages or disadvantages');
  }

  return { strengths, weaknesses, notes };
};

// Get comparative ships (mock data - in real app would query DB)
const getSimilarStealthShips = (tier: string): string[] => {
  const comparisons: Record<string, string[]> = {
    'S-Tier': ['Sabre', 'Eclipse', 'Prowler'],
    'A-Tier': ['Razor EX', 'Hornet Ghost', 'Sentinel'],
    'B-Tier': ['Vanguard Sentinel', 'Herald', '325a'],
    'C-Tier': ['Hornet', 'Gladius', 'Arrow'],
    'D-Tier': ['Cutlass', 'Freelancer', 'Constellation'],
    'F-Tier': ['Caterpillar', 'Starfarer', 'Hull Series']
  };
  
  return comparisons[tier] || [];
};

export const StealthProfilePanel: React.FC<StealthProfilePanelProps> = ({ ship, hardpoints, hardpointOverrides, showComponentSwapNotice = false }) => {
  const stealthTier = useMemo(() => 
    calculateStealthTier(
      ship.armorSignalElectromagnetic,
      ship.armorSignalInfrared,
      ship.armorSignalCrossSection
    ),
    [ship.armorSignalElectromagnetic, ship.armorSignalInfrared, ship.armorSignalCrossSection]
  );

  // Calculate effective cross section for ranking
  const effectiveCrossSection = useMemo(() => 
    calculateEffectiveCrossSection(
      ship.signatureSystemCrossSectionX,
      ship.signatureSystemCrossSectionY,
      ship.signatureSystemCrossSectionZ,
      ship.armorSignalCrossSection
    ),
    [ship.signatureSystemCrossSectionX, ship.signatureSystemCrossSectionY, ship.signatureSystemCrossSectionZ, ship.armorSignalCrossSection]
  );

  const characteristics = useMemo(() => 
    getStealthCharacteristics(
      ship.armorSignalElectromagnetic,
      ship.armorSignalInfrared,
      ship.armorSignalCrossSection
    ),
    [ship.armorSignalElectromagnetic, ship.armorSignalInfrared, ship.armorSignalCrossSection]
  );

  const components = useMemo(() => 
    analyzeComponents(hardpoints, hardpointOverrides),
    [hardpoints, hardpointOverrides]
  );

  const similarShips = useMemo(() => 
    getSimilarStealthShips(stealthTier.tier),
    [stealthTier.tier]
  );

  // Calculate actual cross section value from X/Y/Z dimensions
  const actualCrossSection = useMemo(() => {
    const x = ship.signatureSystemCrossSectionX;
    const y = ship.signatureSystemCrossSectionY;
    const z = ship.signatureSystemCrossSectionZ;
    
    if (x === null || y === null || z === null) return null;
    
    // Cross section is the maximum of the three dimensions
    return Math.max(x, y, z);
  }, [ship.signatureSystemCrossSectionX, ship.signatureSystemCrossSectionY, ship.signatureSystemCrossSectionZ]);

  // Format cross section for display (e.g., 10.2k)
  const formatCrossSection = (value: number): string => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toFixed(0);
  };

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <Eye size={20} />
          <h3>Stealth & Detection Profile</h3>
        </div>
        <div className={styles.headerSubtitle}>
          Hull signature characteristics and detection capabilities
        </div>
      </div>

      {/* Component Swap Notice (only shown in loadout builder) */}
      {showComponentSwapNotice && (
        <div className={styles.componentSwapNotice}>
          <Info size={16} />
          <span>
            <strong>Note:</strong> Component and weapon changes do not currently affect the signature values displayed below. 
            These values reflect the base hull configuration only.
          </span>
        </div>
      )}

      {/* Main Stealth Tier Card */}
      <div className={styles.tierCard}>
        <div className={styles.tierBadge}>
          <span className={`${styles.tier} ${styles[stealthTier.tierClass]}`} style={{ color: stealthTier.color }}>
            {stealthTier.tier}
          </span>
          <span className={styles.tierDescription}>{stealthTier.description}</span>
        </div>
        
        {/* Stealth Ranking for S/A/B tier ships */}
        {(stealthTier.tier === 'S-Tier' || stealthTier.tier === 'A-Tier' || stealthTier.tier === 'B-Tier') && effectiveCrossSection && (
          <div className={styles.stealthRanking}>
            <div className={styles.rankingLabel}>Stealth Radar Signature</div>
            <div className={styles.rankingScore}>
              Effective Cross Section: <span style={{ color: stealthTier.color, fontWeight: 700 }}>
                {effectiveCrossSection < 1000 
                  ? `${effectiveCrossSection.toFixed(0)}m`
                  : `${(effectiveCrossSection / 1000).toFixed(1)}k`}
              </span>
            </div>
            <div className={styles.rankingNote}>
              {stealthTier.tier === 'S-Tier' && '🏆 Elite stealth - Among the smallest radar signatures in the verse'}
              {stealthTier.tier === 'A-Tier' && '⭐ Excellent stealth - Top-tier radar signature for combat ships'}
              {stealthTier.tier === 'B-Tier' && '✓ Good stealth - Above average radar signature reduction'}
            </div>
            <div className={styles.rankingDisclaimer}>
              Based on default loadout
            </div>
          </div>
        )}
      </div>

      {/* Signature Multipliers Grid */}
      <div className={styles.signatureGrid}>
        {/* EM Signature */}
        <div className={styles.signatureCard}>
          <div className={styles.signatureHeader}>
            <Radio size={18} className={styles.emIcon} />
            <span className={styles.signatureTitle}>Electromagnetic</span>
          </div>
          <div className={styles.signatureValue}>
            {ship.armorSignalElectromagnetic !== null && ship.armorSignalElectromagnetic !== 1.0
              ? `${ship.armorSignalElectromagnetic.toFixed(2)}×`
              : <span className={styles.baselineBadge}>Standard</span>
            }
          </div>
          <div className={styles.signatureDescription}>
            {getSignatureDescription(ship.armorSignalElectromagnetic, 'EM')}
          </div>
          <div className={styles.signatureExplainer}>
            Hull property that modifies EM emissions from active components (shields, weapons, power plant).
          </div>
        </div>

        {/* IR Signature */}
        <div className={styles.signatureCard}>
          <div className={styles.signatureHeader}>
            <Thermometer size={18} className={styles.irIcon} />
            <span className={styles.signatureTitle}>Infrared</span>
          </div>
          <div className={styles.signatureValue}>
            {ship.armorSignalInfrared !== null && ship.armorSignalInfrared !== 1.0
              ? `${ship.armorSignalInfrared.toFixed(2)}×`
              : <span className={styles.baselineBadge}>Standard</span>
            }
          </div>
          <div className={styles.signatureDescription}>
            {getSignatureDescription(ship.armorSignalInfrared, 'IR')}
          </div>
          <div className={styles.signatureExplainer}>
            Hull property that modifies heat signature from engines, weapons fire, and component thermal output.
          </div>
        </div>

        {/* Cross Section */}
        <div className={styles.signatureCard}>
          <div className={styles.signatureHeader}>
            <Target size={18} className={styles.csIcon} />
            <span className={styles.signatureTitle}>Cross Section</span>
          </div>
          <div className={styles.signatureValue}>
            {actualCrossSection !== null ? (
              <>
                <span style={{ fontSize: '1.75rem' }}>{formatCrossSection(actualCrossSection)}</span>
                {ship.armorSignalCrossSection !== null && ship.armorSignalCrossSection !== 1.0 && (
                  <>
                    <span style={{ fontSize: '1.2rem', color: '#64748b', margin: '0 8px' }}>→</span>
                    <span style={{ fontSize: '1.75rem', color: ship.armorSignalCrossSection < 1.0 ? '#22c55e' : '#ef4444' }}>
                      {formatCrossSection(actualCrossSection * ship.armorSignalCrossSection)}
                    </span>
                    <span style={{ fontSize: '0.9rem', color: '#94a3b8', marginLeft: '6px' }}>
                      (×{ship.armorSignalCrossSection.toFixed(2)})
                    </span>
                  </>
                )}
              </>
            ) : (
              <span className={styles.baselineBadge}>No Data</span>
            )}
          </div>
          <div className={styles.signatureDescription}>
            {actualCrossSection !== null ? (
              ship.armorSignalCrossSection !== null && ship.armorSignalCrossSection !== 1.0 ? (
                <>
                  Base: {actualCrossSection.toFixed(0)}m → Effective: {(actualCrossSection * ship.armorSignalCrossSection).toFixed(0)}m
                  {ship.armorSignalCrossSection < 1.0 && 
                    ` (${Math.round((1 - ship.armorSignalCrossSection) * 100)}% harder to detect)`
                  }
                  {ship.armorSignalCrossSection > 1.0 && 
                    ` (${Math.round((ship.armorSignalCrossSection - 1) * 100)}% easier to detect)`
                  }
                </>
              ) : (
                `Radar profile dimension: ${actualCrossSection.toFixed(0)} meters (no hull modifier)`
              )
            ) : (
              'Cross section data not available from game files'
            )}
          </div>
          <div className={styles.signatureExplainer}>
            {actualCrossSection !== null && (
              <>
                Physical dimensions: X={ship.signatureSystemCrossSectionX?.toFixed(0)}m, Y={ship.signatureSystemCrossSectionY?.toFixed(0)}m, Z={ship.signatureSystemCrossSectionZ?.toFixed(0)}m
                {ship.armorSignalCrossSection !== null && ship.armorSignalCrossSection !== 1.0 && (
                  <span style={{ display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                    Hull design modifies radar reflectivity by {ship.armorSignalCrossSection.toFixed(2)}×
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stealth Characteristics */}
      {(characteristics.strengths.length > 0 || characteristics.weaknesses.length > 0) && (
        <div className={styles.characteristicsSection}>
          <div className={styles.characteristicsHeader}>
            <Shield size={16} />
            <span>Stealth Characteristics</span>
          </div>

          {characteristics.strengths.length > 0 && (
            <div className={styles.characteristicsList}>
              <div className={styles.characteristicsLabel}>
                <span className={styles.strengthIndicator}>✓</span>
                <span>Strengths</span>
              </div>
              {characteristics.strengths.map((strength, idx) => (
                <div key={idx} className={styles.strengthItem}>
                  {strength}
                </div>
              ))}
            </div>
          )}

          {characteristics.weaknesses.length > 0 && (
            <div className={styles.characteristicsList}>
              <div className={styles.characteristicsLabel}>
                <AlertTriangle size={14} className={styles.weaknessIndicator} />
                <span>Weaknesses</span>
              </div>
              {characteristics.weaknesses.map((weakness, idx) => (
                <div key={idx} className={styles.weaknessItem}>
                  {weakness}
                </div>
              ))}
            </div>
          )}

          {characteristics.notes.length > 0 && (
            <div className={styles.notesSection}>
              {characteristics.notes.map((note, idx) => (
                <div key={idx} className={styles.noteItem}>
                  <Info size={14} />
                  <span>{note}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Component Analysis */}
      <div className={styles.componentAnalysis}>
        <div className={styles.componentHeader}>
          <Zap size={16} />
          <span>Loadout Impact on Stealth</span>
        </div>

        <div className={styles.componentGrid}>
          {/* Coolers */}
          {components.coolers.count > 0 && (
            <div className={styles.componentCard}>
              <Snowflake size={16} className={styles.coolerIcon} />
              <div className={styles.componentInfo}>
                <span className={styles.componentLabel}>
                  {components.coolers.count} Cooler{components.coolers.count !== 1 ? 's' : ''}
                </span>
                <span className={styles.componentValue}>
                  {Math.round(components.coolers.totalCooling).toLocaleString()} cooling/s
                </span>
                <span className={styles.componentNote}>
                  {components.coolers.totalCooling > 10000 
                    ? 'Excellent heat management - supports stealth operations'
                    : components.coolers.totalCooling > 5000
                    ? 'Good cooling capacity for IR signature control'
                    : 'Basic cooling - may struggle with sustained stealth'
                  }
                </span>
              </div>
            </div>
          )}

          {/* Power Plants */}
          {components.powerPlants.count > 0 && (
            <div className={styles.componentCard}>
              <Zap size={16} className={styles.powerIcon} />
              <div className={styles.componentInfo}>
                <span className={styles.componentLabel}>
                  {components.powerPlants.count} Power Plant{components.powerPlants.count !== 1 ? 's' : ''}
                </span>
                <span className={styles.componentValue}>
                  {Math.round(components.powerPlants.totalPower).toLocaleString()} output
                </span>
                <span className={styles.componentNote}>
                  {components.powerPlants.avgSize >= 3
                    ? 'Large power plants increase EM signature when active'
                    : components.powerPlants.avgSize >= 2
                    ? 'Medium power plants - balanced EM footprint'
                    : 'Small power plants support low EM operations'
                  }
                </span>
              </div>
            </div>
          )}

          {/* Shields */}
          {components.shields > 0 && (
            <div className={styles.componentCard}>
              <Shield size={16} className={styles.shieldIcon} />
              <div className={styles.componentInfo}>
                <span className={styles.componentLabel}>
                  {components.shields} Shield{components.shields !== 1 ? 's' : ''}
                </span>
                <span className={styles.componentNote}>
                  Active shields increase EM signature - can be powered down for stealth
                </span>
              </div>
            </div>
          )}

          {/* Weapons */}
          {components.weapons > 0 && (
            <div className={styles.componentCard}>
              <Target size={16} className={styles.weaponIcon} />
              <div className={styles.componentInfo}>
                <span className={styles.componentLabel}>
                  {components.weapons} Weapon{components.weapons !== 1 ? 's' : ''}
                </span>
                <span className={styles.componentNote}>
                  Weapons generate high EM/IR when active - power down when not in combat
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Similar Ships */}
      {similarShips.length > 0 && (
        <div className={styles.comparisonSection}>
          <div className={styles.comparisonHeader}>
            <span>Similar stealth profile to:</span>
          </div>
          <div className={styles.comparisonShips}>
            {similarShips.map((shipName, idx) => (
              <span key={idx} className={styles.comparisonShip}>
                {shipName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className={styles.infoNote}>
        <Info size={14} />
        <span>
          These values are hull-based signature multipliers from ship data. 
          Actual detection ranges vary based on active components, power allocation, and enemy radar capabilities.
        </span>
      </div>
    </div>
  );
};
